import { Link } from 'react-router-dom';
import { TERMS_VERSION, TERMS_EFFECTIVE } from '../legal/terms';

function Terms() {
  return (
    <div className="page-container">
      <Link to="/" className="back-button">← Back to Home</Link>

      <h1>Terms of Service</h1>
      <div className="content-card">
        <div className="terms-version-banner">
          <span>Version <strong>{TERMS_VERSION}</strong></span>
          <span>Effective <strong>{TERMS_EFFECTIVE}</strong></span>
        </div>

        <section>
          <p>
            These Terms of Service ("Terms") govern your access to and use of the LangShazam website at{' '}
            <a href="https://www.langshazam.com">https://www.langshazam.com</a> and any related services
            (collectively, the "Service"). By accessing or using the Service you agree to be bound by these
            Terms. If you do not agree, do not use the Service.
          </p>
        </section>

        <section>
          <h3>Accepting These Terms</h3>
          <p>
            Creating a LangShazam account requires you to accept these Terms and our{' '}
            <Link to="/privacy">Privacy Policy</Link> by ticking the agreement box at sign-up. We record
            the Terms version you accepted, the time of acceptance, and the network address it came
            from, and show that record on your <Link to="/account">account page</Link>. If we change these
            Terms materially we publish a new version and ask you to accept it the next time you sign
            in; you can decline by signing out and closing your account. The language detector requires
            an account, so it can only be used after accepting the current Terms. Langle can be played
            without an account, and using it means you accept these Terms as published.
          </p>
        </section>

        <section>
          <h3>1. Description of Service</h3>
          <p>
            LangShazam is a browser-based tool that detects the spoken language present in audio captured
            from your device's microphone. The Service uses real-time speech-to-text transcription combined
            with a language-detection model to identify the language being spoken. The Service is provided
            free of charge.
          </p>
        </section>

        <section>
          <h3>2. Eligibility</h3>
          <p>
            You must be at least 13 years old to use the Service. By using the Service, you represent and
            warrant that you meet this requirement and that you have the legal capacity to enter into
            these Terms.
          </p>
        </section>

        <section>
          <h3>3. Acceptable Use</h3>
          <p>You agree NOT to:</p>
          <ul>
            <li>
              Use the Service to record or transmit audio of any person without their knowledge or
              consent where such consent is required by law.
            </li>
            <li>Use the Service to violate any applicable local, national, or international law.</li>
            <li>
              Use the Service to harass, defame, or harm any individual or group, or to upload content
              you do not have the right to use.
            </li>
            <li>
              Attempt to reverse engineer, scrape, copy, or otherwise abuse the Service, our APIs, or our
              infrastructure.
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the Service, including by
              automated means, denial-of-service attempts, or excessive request rates.
            </li>
            <li>Resell, sublicense, or commercially redistribute output from the Service without permission.</li>
          </ul>
          <p>
            You are solely responsible for obtaining any consent or rights required to record audio in
            your jurisdiction. Many regions have one-party or two-party consent laws governing audio
            recording — make sure you understand and comply with the rules that apply to you.
          </p>
        </section>

        <section>
          <h3>4. Privacy</h3>
          <p>
            Your use of the Service is also governed by our{' '}
            <Link to="/privacy">Privacy Policy</Link>, which explains what data we collect, how we use it,
            and what choices you have. By using the Service, you consent to the data practices described
            there.
          </p>
        </section>

        <section>
          <h3>5. Intellectual Property</h3>
          <p>
            All content, branding, code, and design of the Service — except for content you provide and
            third-party components used under license — are owned by LangShazam and are protected by
            copyright, trademark, and other intellectual property laws. You receive no rights to our
            intellectual property except the limited right to use the Service in accordance with these
            Terms.
          </p>
          <p>
            You retain ownership of audio you provide. By using the Service, you grant LangShazam a
            limited, non-exclusive license to process that audio solely to operate, maintain, and improve
            the Service, as described in our Privacy Policy.
          </p>
        </section>

        <section>
          <h3>6. Third-Party Services and Advertising</h3>
          <p>
            The Service relies on third-party providers for functions such as transcription, hosting,
            and advertising. We are not responsible for the content, policies, or practices of those
            third parties. Advertisements displayed on the Service are provided by Google AdSense and
            may be personalized based on cookies as described in our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </section>

        <section>
          <h3>7. Disclaimers</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
            IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            NON-INFRINGEMENT, OR ACCURACY OF DETECTION. Language identification is probabilistic and may
            be incorrect, especially for short utterances, mixed-language audio, dialectal variation, or
            poor audio quality. You should not rely on the Service for any safety-critical, legal,
            medical, or commercial decision without independent verification.
          </p>
        </section>

        <section>
          <h3>8. Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL LANGSHAZAM, ITS OPERATORS,
            CONTRIBUTORS, OR SUPPLIERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING FROM OR RELATING
            TO YOUR USE OF OR INABILITY TO USE THE SERVICE — EVEN IF WE HAVE BEEN ADVISED OF THE
            POSSIBILITY OF SUCH DAMAGES. Our total cumulative liability arising out of these Terms or the
            Service shall not exceed USD $50.
          </p>
        </section>

        <section>
          <h3>9. Indemnification</h3>
          <p>
            You agree to indemnify and hold harmless LangShazam from any claim, demand, loss, or expense
            (including reasonable attorneys' fees) arising out of your use of the Service, your violation
            of these Terms, or your violation of any third-party right, including any audio you record or
            submit through the Service.
          </p>
        </section>

        <section>
          <h3>10. Termination</h3>
          <p>
            We may suspend or terminate your access to the Service at any time, with or without notice,
            for any reason — including violation of these Terms. You may stop using the Service at any
            time. Sections that by their nature should survive termination (e.g. Sections 5, 7, 8, 9, 11)
            will survive.
          </p>
        </section>

        <section>
          <h3>11. Governing Law</h3>
          <p>
            These Terms are governed by the laws of the jurisdiction in which the Service operator
            resides, without regard to its conflict-of-laws rules. Any dispute will be resolved in the
            competent courts of that jurisdiction, unless mandatory consumer-protection laws of your
            country of residence provide otherwise.
          </p>
        </section>

        <section>
          <h3>12. Changes to These Terms</h3>
          <p>
            We may update these Terms from time to time. The version and effective date at the top
            identify the current revision. For material changes we ask signed-in users to accept the new
            version before continuing; for other changes, continued use of the Service after the
            effective date constitutes acceptance.
          </p>
        </section>

        <section>
          <h3>13. Contact</h3>
          <p>
            Questions about these Terms? Email{' '}
            <a href="mailto:support@langshazam.com">support@langshazam.com</a> or visit the{' '}
            <Link to="/contact">Contact page</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}

export default Terms;
